/**
 * Build MapLibre sprites in the browser from a recolourable SVG icon set
 * (public/icon-sets/<id>, produced by scripts/import-figma-icons.mjs).
 *
 * Template tokens: {{outline}} (pin shape), {{accent}} (coloured disc),
 * {{glyph}} (symbol on the disc). `fixed` icons are drawn unchanged.
 */

export type IconSetManifest = {
  id: string;
  name: string;
  description: string;
  version: number;
  /** Height of a pin in the design (Figma frame), px. */
  designHeight: number;
  /** false → the icons have no outline shape (the editor hides that colour). */
  hasOutline?: boolean;
  colors: { outline: string; glyph: string };
  groups: { id: string; label: string; color: string; alsoColors?: string[] }[];
  icons: Record<string, { group?: string; accent?: string; fixed?: boolean }>;
  /** Sample POI name per icon, shown under it in the editor preview. */
  labels?: Record<string, string>;
};

export type IconSize = "s" | "m" | "l";

/** Map background the label colours are meant for. */
export type TextVariant = "light" | "dark";

/** POI label colours: `all` overrides every group, `groups` override `all`. */
export type TextColors = { all?: string; groups: Record<string, string> };

/** What the user can change. Group / text colours only apply once set. */
export type IconColorConfig = {
  outline: string;
  glyph: string;
  groups: Record<string, string>;
  size: IconSize;
  /** Label colours per map background (absent in configs saved before). */
  text?: Record<TextVariant, TextColors>;
};

/** Pin height on the map in CSS px — "m" matches the Vietmap icons (20×24). */
export const ICON_SIZE_PX: Record<IconSize, number> = { s: 20, m: 24, l: 30 };

export function defaultIconConfig(manifest: IconSetManifest): IconColorConfig {
  return {
    outline: manifest.colors.outline,
    glyph: manifest.colors.glyph,
    groups: {},
    size: "m",
    text: { light: { groups: {} }, dark: { groups: {} } },
  };
}

/** True for dark CSS colours (`#rrggbb`, `#rgb`, `rgb[a](…)`) — picks the
 *  label colour variant from a style's background. */
export function isDarkColor(css: string): boolean {
  let rgb: number[] | null = null;
  const hex = css.match(/^#([0-9a-f]{3}|[0-9a-f]{6})$/i);
  if (hex) {
    const h = hex[1].length === 3 ? hex[1].replace(/./g, "$&$&") : hex[1];
    rgb = [0, 2, 4].map((i) => parseInt(h.slice(i, i + 2), 16));
  } else {
    const m = css.match(/rgba?\(\s*([\d.]+)\s*,\s*([\d.]+)\s*,\s*([\d.]+)/i);
    if (m) rgb = [m[1], m[2], m[3]].map(Number);
  }
  if (!rgb) return false;
  return (0.299 * rgb[0] + 0.587 * rgb[1] + 0.114 * rgb[2]) / 255 < 0.5;
}

/** Blend a hex colour towards `to` by `amount` (0–1). */
export function mixHex(from: string, to: string, amount: number): string {
  const parse = (h: string) => [1, 3, 5].map((i) => parseInt(h.slice(i, i + 2), 16));
  const a = parse(from);
  const b = parse(to);
  return "#" + a.map((v, i) => Math.round(v + (b[i] - v) * amount).toString(16).padStart(2, "0")).join("").toUpperCase();
}

/**
 * Default label colour of a group: its icon colour on light maps, lifted
 * towards white on dark maps so it stays readable.
 */
export function defaultTextColor(manifest: IconSetManifest, groupId: string, variant: TextVariant): string {
  const color = manifest.groups.find((g) => g.id === groupId)?.color ?? "#64748B";
  return variant === "dark" ? mixHex(color, "#FFFFFF", 0.45) : color;
}

/** Label colour a group gets with this config (override → "all" → default). */
export function groupTextColor(
  manifest: IconSetManifest,
  config: IconColorConfig,
  groupId: string,
  variant: TextVariant
): string {
  const text = config.text?.[variant];
  return text?.groups[groupId] || text?.all || defaultTextColor(manifest, groupId, variant);
}

/** Label colour for the POI using icon `name`; null for fixed / unknown icons. */
export function iconTextColor(
  manifest: IconSetManifest,
  config: IconColorConfig,
  name: string,
  variant: TextVariant
): string | null {
  const group = manifest.icons[name]?.group;
  return group ? groupTextColor(manifest, config, group, variant) : null;
}

/** 2: stroke compensation, exact-size rasterising, retina file at 3×. */
const SPRITE_RENDERER_VERSION = 2;

/** Stable JSON (sorted keys, normalised hex) of what goes into the sprite —
 *  label colours are excluded, they live in the style's layers. */
export function canonicalConfig(config: IconColorConfig): string {
  const groups = Object.fromEntries(
    Object.entries(config.groups)
      .filter(([, v]) => !!v)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([k, v]) => [k, v.toUpperCase()])
  );
  return JSON.stringify({
    glyph: config.glyph.toUpperCase(),
    groups,
    outline: config.outline.toUpperCase(),
    // Bumped whenever rasterising changes, so old cached sprites aren't reused.
    renderer: SPRITE_RENDERER_VERSION,
    size: config.size,
  });
}

// ── Loading ──────────────────────────────────────────────────────────────────

const base = () => ((import.meta as any).env.BASE_URL as string) || "/";
const manifestCache = new Map<string, Promise<IconSetManifest>>();
const templateCache = new Map<string, Promise<string>>();

export function iconSetBaseUrl(setPath: string): string {
  return /^https?:\/\//.test(setPath) ? setPath.replace(/\/$/, "") : `${base()}${setPath.replace(/^\/|\/$/g, "")}`;
}

export function loadIconSetManifest(setPath: string): Promise<IconSetManifest> {
  const url = `${iconSetBaseUrl(setPath)}/manifest.json`;
  let p = manifestCache.get(url);
  if (!p) {
    p = fetch(url).then((r) => {
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      return r.json();
    });
    p.catch(() => manifestCache.delete(url));
    manifestCache.set(url, p);
  }
  return p;
}

function loadTemplate(setPath: string, name: string, version: number): Promise<string> {
  const url = `${iconSetBaseUrl(setPath)}/svg/${encodeURIComponent(name)}.svg?v=${version}`;
  let p = templateCache.get(url);
  if (!p) {
    p = fetch(url).then((r) => {
      if (!r.ok) throw new Error(`HTTP ${r.status} (${name})`);
      return r.text();
    });
    p.catch(() => templateCache.delete(url));
    templateCache.set(url, p);
  }
  return p;
}

export async function loadIconTemplates(setPath: string, manifest: IconSetManifest): Promise<Record<string, string>> {
  const names = Object.keys(manifest.icons);
  const svgs = await Promise.all(names.map((n) => loadTemplate(setPath, n, manifest.version)));
  return Object.fromEntries(names.map((n, i) => [n, svgs[i]]));
}

// ── Rendering ────────────────────────────────────────────────────────────────

/** Fill one template with colours. */
export function renderIconSvg(
  manifest: IconSetManifest,
  name: string,
  template: string,
  config: IconColorConfig
): string {
  const icon = manifest.icons[name];
  if (!icon || icon.fixed) return template;
  const accent = (icon.group && config.groups[icon.group]) || icon.accent || "#888888";
  return template
    .split("{{outline}}").join(config.outline)
    .split("{{accent}}").join(accent)
    .split("{{glyph}}").join(config.glyph);
}

export function svgDataUrl(svg: string): string {
  return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
}

function svgSize(svg: string): { width: number; height: number } {
  const w = svg.match(/<svg[^>]*\bwidth="([\d.]+)"/);
  const h = svg.match(/<svg[^>]*\bheight="([\d.]+)"/);
  return { width: w ? Number(w[1]) : 24, height: h ? Number(h[1]) : 24 };
}

async function loadImage(src: string): Promise<HTMLImageElement> {
  const img = new Image();
  img.decoding = "async";
  img.src = src;
  await img.decode();
  return img;
}

export type BuiltSprite = {
  pixelRatio: number;
  json: Record<string, { x: number; y: number; width: number; height: number; pixelRatio: number }>;
  png: Blob;
};

/** Thinnest glyph stroke we let through, in CSS px on the map. */
const MIN_STROKE_CSS_PX = 1.1;

/**
 * Icons are drawn large in Figma (44×54 pin, 1.5 px glyph strokes) and shown
 * at ~24 px, where a 1.5 px stroke shrinks to ~0.67 px — thinner than a pixel,
 * so it breaks up. Thicken the glyph strokes (only those after the coloured
 * disc; outline/disc are fills) so they stay ≥ MIN_STROKE_CSS_PX, capped at
 * 2.2× so small glyph details don't clog.
 */
export function thickenStrokes(svg: string, scale: number): string {
  const disc = svg.search(/<rect\b[^>]*\brx="17"/);
  if (disc < 0) return svg;
  const glyph = svg.slice(disc).replace(/stroke-width="([\d.]+)"/g, (_m, w: string) => {
    const width = Number(w);
    const factor = Math.min(2.2, Math.max(1, MIN_STROKE_CSS_PX / (width * scale)));
    return `stroke-width="${+(width * factor).toFixed(2)}"`;
  });
  return svg.slice(0, disc) + glyph;
}

/** Give the root <svg> an exact pixel size so it rasterises at that size
 *  (no draw-large-then-shrink resampling). viewBox keeps the drawing. */
function sizeSvg(svg: string, width: number, height: number): string {
  return svg.replace(/<svg\b([^>]*)>/, (_m, attrs: string) => {
    const rest = attrs.replace(/\s(width|height)="[^"]*"/g, "");
    return `<svg width="${width}" height="${height}"${rest}>`;
  });
}

/**
 * Rasterise every icon and pack them into one atlas (shelf packing, tallest
 * first). Sizes are scaled so a pin is ICON_SIZE_PX tall.
 *
 * `pixelRatio` = device pixels drawn per CSS px (written into the atlas).
 * MapLibre honours any value, so the @2x file is built at 3: icons stay sharp
 * when styles enlarge them with `icon-size` up to 1.5 at high zoom.
 */
export async function buildSprite(
  manifest: IconSetManifest,
  templates: Record<string, string>,
  config: IconColorConfig,
  pixelRatio: number
): Promise<BuiltSprite> {
  const scale = ICON_SIZE_PX[config.size] / manifest.designHeight;
  const items = await Promise.all(
    Object.keys(manifest.icons).map(async (name) => {
      const raw = thickenStrokes(renderIconSvg(manifest, name, templates[name], config), scale);
      const size = svgSize(raw);
      const width = Math.max(1, Math.round(size.width * scale * pixelRatio));
      const height = Math.max(1, Math.round(size.height * scale * pixelRatio));
      return { name, width, height, img: await loadImage(svgDataUrl(sizeSvg(raw, width, height))) };
    })
  );

  // Shelf packing — good enough for ~70 similar-sized icons.
  items.sort((a, b) => b.height - a.height || a.name.localeCompare(b.name));
  const maxWidth = 512 * pixelRatio;
  const GAP = 2 * pixelRatio;
  let x = 0;
  let y = 0;
  let shelf = 0;
  let width = 0;
  const placed = items.map((it) => {
    if (x + it.width > maxWidth) {
      x = 0;
      y += shelf + GAP;
      shelf = 0;
    }
    const pos = { ...it, x, y };
    x += it.width + GAP;
    shelf = Math.max(shelf, it.height);
    width = Math.max(width, x);
    return pos;
  });
  const height = y + shelf;

  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Canvas 2D không khả dụng");
  const json: BuiltSprite["json"] = {};
  for (const p of placed) {
    ctx.drawImage(p.img, p.x, p.y, p.width, p.height);
    json[p.name] = { x: p.x, y: p.y, width: p.width, height: p.height, pixelRatio };
  }
  const png = await new Promise<Blob>((resolve, reject) =>
    canvas.toBlob((b) => (b ? resolve(b) : reject(new Error("Không tạo được PNG"))), "image/png")
  );
  return { pixelRatio, json, png };
}
